import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Button,
  InputAdornment,
  Chip,
  Stack,
  Alert
} from '@mui/material';
import {
  Search,
  Business,
  Fingerprint,
  Info,
  Home,
  TrendingUp,
  Warning,
  Shield
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { searchVendors, setSearchType, clearSearch } from '../../store/slices/vendorSlice';
import VendorSearchResults from './VendorSearchResults';
import VendorDetailModal from './VendorDetailModal';
import VendorAutocomplete from './VendorAutocomplete';

const VendorVerification: React.FC = () => {
  const dispatch = useAppDispatch();
  const { searchType, sortBy, loading, error, totalFound, searchQuery } = useAppSelector(state => state.vendor);
  const [query, setQuery] = useState('');
  const [showHelp, setShowHelp] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleSearch = (searchQuery?: string) => {
    const searchTerm = searchQuery || query.trim();
    if (searchTerm) {
      // When search type is 'auto', detect if it's a UEI
      let typeToUse = searchType;
      if (searchType === 'auto') {
        const isUEI = /^[A-Z0-9]{12}$/i.test(searchTerm);
        typeToUse = isUEI ? 'uei' : 'name';
      }
      
      dispatch(searchVendors({
        query: searchTerm,
        search_type: typeToUse,
        limit: 50,
        sort_by: sortBy
      }));
      
      if (searchQuery) {
        setQuery(searchTerm);
        // Don't change the search type when using autocomplete
        // Keep it as 'auto' unless user explicitly changes it
      }
      setShowHelp(false);
    }
  };

  const exampleVendors = [
    { name: 'LB5KVANFKPY7', icon: <Warning fontSize="small" />, color: 'error' as const, description: 'L3Harris - Critical Risk' },
    { name: 'Booz Allen Hamilton', icon: <Business fontSize="small" />, color: 'info' as const, description: 'Consulting firm' },
    { name: 'EMPIRE FACILITY SERVICES', icon: <TrendingUp fontSize="small" />, color: 'success' as const, description: 'Small business growth' },
    { name: 'EMPIRE HOSPITALITY', icon: <Home fontSize="small" />, color: 'success' as const, description: 'Hospitality services' },
    { name: 'MD7WWJFA6LM1', icon: <Fingerprint fontSize="small" />, color: 'secondary' as const, description: 'Search by UEI' },
    { name: 'Lockheed Martin', icon: <Shield fontSize="small" />, color: 'primary' as const, description: 'Largest defense contractor' },
    { name: 'Raytheon', icon: <Shield fontSize="small" />, color: 'info' as const, description: 'Aerospace & defense' },
    { name: 'CACI International', icon: <Business fontSize="small" />, color: 'warning' as const, description: 'IT services' },
    { name: 'AECOM', icon: <Warning fontSize="small" />, color: 'error' as const, description: 'Recent fraud case' },
  ];

  const handleSearchTypeChange = (_event: React.MouseEvent<HTMLElement>, newType: string | null) => {
    if (newType) {
      dispatch(setSearchType(newType as 'name' | 'uei' | 'auto'));
    }
  };

  const handleClearSearch = () => {
    setQuery('');
    dispatch(clearSearch());
    setShowHelp(true);
  };


  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Collapsible Sidebar Indicator */}
      {isCollapsed && (
        <Typography 
          variant="caption" 
          sx={{ 
            position: 'absolute',
            left: 8,
            top: 8,
            color: 'text.secondary',
            fontSize: '0.7rem'
          }}
        >
          Collapse sidebar
        </Typography>
      )}



      {/* Search Interface */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ pb: 2 }}>
          
          {/* Search Type Toggle */}
          <ToggleButtonGroup
            value={searchType}
            exclusive
            onChange={handleSearchTypeChange}
            sx={{ mb: 3 }}
            size="small"
          >
            <ToggleButton value="auto" sx={{ px: 2 }}>
              <Search sx={{ mr: 1, fontSize: '1.2rem' }} />
              AUTO DETECT
            </ToggleButton>
            <ToggleButton value="name" sx={{ px: 2 }}>
              <Business sx={{ mr: 1, fontSize: '1.2rem' }} />
              BY NAME
            </ToggleButton>
            <ToggleButton value="uei" sx={{ px: 2 }}>
              <Fingerprint sx={{ mr: 1, fontSize: '1.2rem' }} />
              BY UEI
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Search Input with Autocomplete */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <VendorAutocomplete
              fullWidth
              placeholder="Enter vendor name or UEI (12-character identifier)"
              onSelect={(vendor) => {
                if (vendor) {
                  // Use UEI if available, otherwise use vendor name
                  const searchTerm = vendor.uei || vendor.vendor_name;
                  setQuery(searchTerm);
                  handleSearch(searchTerm);
                }
              }}
              onInputChange={(value) => {
                // Update query as user types
                setQuery(value);
              }}
            />
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={!query.trim() || loading}
              size="large"
              sx={{ minWidth: 120 }}
            >
              {loading ? 'Searching...' : 'Search'}
            </Button>
            {(searchQuery || totalFound > 0) && (
              <Button
                variant="outlined"
                onClick={handleClearSearch}
                disabled={loading}
                size="large"
              >
                Clear
              </Button>
            )}
          </Box>

          {/* Results Summary */}
          {searchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {loading ? 'Searching...' : `Searched for "${searchQuery}" - Found ${totalFound} result${totalFound !== 1 ? 's' : ''}`}
            </Typography>
          )}
        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}


        {/* Features Section */}
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* AI-Enhanced Verification */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip
              icon={<Shield />}
              label="AI-Enhanced Verification"
              color="primary"
              variant="outlined"
              sx={{ cursor: 'default' }}
            />
            <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary' }}>
              AI-powered semantic analysis for better fraud detection
            </Typography>
          </Box>
        </Box>
        </CardContent>
      </Card>

      {/* Search Results */}
      <VendorSearchResults />

      {/* Example Searches Section */}
      {!loading && !searchQuery && (
        <Card sx={{ mb: 3, boxShadow: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
              <Info sx={{ fontSize: '1.2rem' }} />
              Example Investigations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Try these example queries to see how vendor verification works:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {exampleVendors.slice(0, 5).map((vendor) => (
                <Chip
                  key={vendor.name}
                  label={vendor.name}
                  onClick={() => handleSearch(vendor.name)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'white'
                    }
                  }}
                />
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Welcome State / Ready to Investigate */}
      {!loading && !searchQuery && (
        <Card sx={{ textAlign: 'center', p: 4, backgroundColor: 'background.paper' }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            Vendor Verification System
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
            Enter a vendor name or UEI above or click on one of the example investigations to get started.
            This tool will verify vendor eligibility and analyze their award history for potential risks.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              onClick={() => handleSearch(exampleVendors[0].name)}
            >
              Try Example Search
            </Button>
            <Button 
              variant="outlined" 
              onClick={() => window.location.href = '/'}
            >
              Back to Standard Search
            </Button>
          </Box>
        </Card>
      )}

      {/* Detail Modal */}
      <VendorDetailModal />
    </Container>
  );
};

export default VendorVerification;